import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';

import {
  DatePeriodInTimestamp,
  FilterPreset,
  RangePicker as RangePickerFromDS,
  RangePickerProps as RangePickerPropsFromDS,
} from '@cast/design-system';
import { DateRange } from '@cast/shared-types';

import { allFilters } from '../constants/time-range';
import { DateRangeFilter } from '../types/time-range';

import { RangePickerOptions } from './types';
import {
  useReactRouterStrategy,
  useReactStateStrategy,
} from './useManageRangePickerState';
import { getDateRangeFilterFromValue } from './utils';

export type RangePickerProps = Partial<
  Omit<
    RangePickerPropsFromDS,
    'selectedRange' | 'filterPresets' | 'onChange' | 'initialPreset'
  >
> &
  RangePickerOptions & {
    dateRange?: DateRange;
    hideTimezonePicker?: boolean;
    onChange?: (
      range: DatePeriodInTimestamp,
      timezone: string,
      preset?: DateRangeFilter
    ) => void;
  };

export type RangePickerRef = {
  /**
   * Programmatically set the picker's selection WITHOUT remounting.
   * - If `preset` is provided, the strategy selects that preset (resolving to
   *   concrete dates) and clears any explicit from/to.
   * - Otherwise `from`/`to` set an explicit custom range.
   * `timezone` should always be provided.
   */
  setRange: (params: {
    from?: string;
    to?: string;
    timezone?: string;
    preset?: DateRangeFilter;
  }) => void;
};

export const RangePicker = forwardRef<RangePickerRef, RangePickerProps>(
  (
    {
      filterPresets,
      queryParamKeys,
      initialPreset,
      maxNumberOfDays,
      initialRange,
      initialTimezone = dayjs.tz.guess(),
      allowTimeInput = false,
      hideTimezonePicker = false,
      ...rest
    },
    ref
  ) => {
    const { getState, setState } = (
      queryParamKeys
        ? useReactRouterStrategy.bind(null, {
            initialRange,
            initialPreset,
            initialTimezone,
            queryParamKeys,
            maxNumberOfDays,
          })
        : useReactStateStrategy.bind(null, {
            initialRange,
            initialPreset,
            initialTimezone,
            maxNumberOfDays,
          })
    )();

    const { currentDateRange, currentDateRangeFilter, timezone } =
      useMemo(() => {
        const { currentDateRange, currentDateRangeFilter, timezone } =
          getState();

        // prefer to use user selected preset
        if (currentDateRangeFilter && currentDateRangeFilter in allFilters) {
          return {
            currentDateRange: allFilters[currentDateRangeFilter].date(
              timezone,
              allowTimeInput
            ),
            currentDateRangeFilter,
            timezone,
          };
        }

        // fallback to use user selected date range
        if (
          currentDateRange.from &&
          currentDateRange.to &&
          dayjs(currentDateRange.from).isValid() &&
          dayjs(currentDateRange.to).isValid()
        ) {
          // if filterPresets are provided, use the filter from the date range
          if (filterPresets?.length) {
            const presetFromDateRange = getDateRangeFilterFromValue({
              value: [dayjs(currentDateRange.from), dayjs(currentDateRange.to)],
              timezone,
              filters: filterPresets || [],
              allowTimeInput,
            });
            // there is preset with the same date range
            if (presetFromDateRange) {
              return {
                currentDateRange: { from: undefined, to: undefined },
                currentDateRangeFilter: presetFromDateRange,
                timezone,
              };
            }
          }

          return { currentDateRange, currentDateRangeFilter, timezone };
        }

        // fallback to default filter preset
        if (initialPreset && initialPreset in allFilters) {
          return {
            currentDateRange: allFilters[initialPreset].date(
              timezone,
              allowTimeInput
            ),
            currentDateRangeFilter: initialPreset,
            timezone,
          };
        }

        // the ultimate fallback - this will set currentDateRange to today's date
        return { currentDateRange, currentDateRangeFilter, timezone };
      }, [getState, initialPreset, filterPresets, allowTimeInput]);

    const handleChange = useCallback(
      (
        { from, to }: DatePeriodInTimestamp,
        timezone: string,
        preset?: FilterPreset
      ) => {
        const selectedPreset = preset
          ? (Object.keys(allFilters).find((key) =>
              isEqual(allFilters[key as DateRangeFilter], preset)
            ) as DateRangeFilter)
          : undefined;

        setState({
          currentDateRange: {
            from: dayjs(from).toISOString(),
            to: dayjs(to).toISOString(),
          },
          currentDateRangeFilter: selectedPreset,
          timezone,
        });

        rest.onChange?.({ from, to }, timezone, selectedPreset);
      },
      [setState, rest]
    );

    const filters = useMemo(() => {
      return filterPresets?.map((key) => allFilters[key]);
    }, [filterPresets]);

    useImperativeHandle(
      ref,
      () => ({
        setRange: ({ from, to, timezone, preset }) => {
          setState({
            currentDateRange: { from, to },
            currentDateRangeFilter: preset,
            timezone,
          });
        },
      }),
      [setState]
    );

    return (
      <RangePickerFromDS
        initialRange={{
          from: currentDateRange.from,
          to: currentDateRange.to,
        }}
        inputValue={() => {
          const offsetInMinutes = dayjs().tz(timezone).utcOffset();

          const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
          const minutes = Math.abs(offsetInMinutes) % 60;
          const gmtOffset = `GMT ${offsetInMinutes >= 0 ? '+' : '-'}${String(
            hours
          ).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

          if (currentDateRangeFilter) {
            return `${allFilters[currentDateRangeFilter]?.label} ${gmtOffset}`;
          }
          const from = dayjs(currentDateRange.from)
            .tz(timezone)
            .format('YYYY/MM/DD');
          const to = dayjs(currentDateRange.to)
            .tz(timezone)
            .format('YYYY/MM/DD');

          return `${from} - ${to} ${gmtOffset}`;
        }}
        maxNumberOfDays={maxNumberOfDays || 31}
        filterPresets={filters}
        initialTimezone={timezone}
        hideTimezonePicker={hideTimezonePicker}
        allowTimeInput={allowTimeInput}
        {...rest}
        onChange={handleChange}
      />
    );
  }
);

RangePicker.displayName = 'RangePicker';
